/**
 * API 실패를 사용자에게 알리는 공통 처리.
 *
 * 왜 필요한가:
 * 이 프로젝트의 `.catch` 145곳 중 122곳이 `console.log(error)` 한 줄뿐이었다.
 * 모바일에는 콘솔이 없으므로 사용자 눈에는 **버튼을 눌렀는데 아무 일도 안 일어나는**
 * 것으로만 보인다. 그래서 같은 동작을 반복하게 되고, 그것이 예약·설문 중복 접수로 이어졌다.
 *
 * 특히 전역 타임아웃이 짧아(예전 5초) 지하철·엘리베이터·약한 LTE 에서는 정상 요청도
 * 자주 끊겼다. 끊긴 이유를 알려주지 않으면 사용자는 앱이 고장 난 것으로 받아들인다.
 *
 * 알림 수단으로 `alert` 를 쓰는 이유: 이 앱의 안내가 전부 alert 이라 표현을 맞췄다.
 * (WebView 가 JS 대화상자를 구현하지 않으면 무시될 수 있다 — 이슈목록 V-3 참고.
 *  Dialog/Snackbar 로 옮길 때는 이 파일 한 곳만 바꾸면 된다.)
 */

/** 실패의 종류 — 사용자에게 다른 행동을 안내해야 하는 단위로만 나눈다 */
export type ApiFailureKind =
  /** 응답이 너무 늦다 (axios timeout) */
  | 'timeout'
  /** 서버에 닿지 못했다 (오프라인·DNS·CORS 차단) */
  | 'offline'
  /** 인증 거부 (401 / 403) */
  | 'unauthorized'
  /** 대상 없음 (404) */
  | 'notfound'
  /** 서버 오류 (5xx) */
  | 'server'
  /** 그 외 응답 오류 (4xx 등) */
  | 'unknown';

export function classifyApiError(error: any): ApiFailureKind {
  // axios 는 타임아웃을 ECONNABORTED 로 준다 (구버전은 message 로만 구분됐다)
  if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
    return 'timeout';
  }

  const status = error?.response?.status;
  if (typeof status === 'number') {
    if (status === 401 || status === 403) {
      return 'unauthorized';
    }
    if (status === 404) {
      return 'notfound';
    }
    if (status >= 500) {
      return 'server';
    }
    return 'unknown';
  }

  // 여기부터는 응답이 없는 경우다. 두 가지가 섞여 들어온다.
  //
  //  (1) 서버에 닿지 못한 진짜 통신 실패 → 연결을 확인하라고 안내해야 한다
  //  (2) `.then` 안에서 던져진 예외 (JSON.parse 실패, 속성 접근 TypeError 등)
  //      → 통신은 됐다. 이걸 '네트워크에 연결할 수 없습니다' 로 안내하면 거짓말이고,
  //        사용자는 원인과 상관없는 조치(와이파이 확인)를 반복하게 된다.
  //
  // axios 가 만든 오류에는 isAxiosError / request 가 붙어 있어 (1)을 가려낼 수 있다.
  if (error?.isAxiosError === true || error?.request !== undefined) {
    return 'offline';
  }
  if (error instanceof Error) {
    return 'unknown';
  }
  return 'offline';
}

const MESSAGE: { [key in ApiFailureKind]: string } = {
  timeout: '서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.',
  offline: '네트워크에 연결할 수 없습니다. 연결 상태를 확인한 뒤 다시 시도해 주세요.',
  unauthorized: '로그인 정보가 만료되었습니다. 다시 로그인한 뒤 시도해 주세요.',
  notfound: '요청한 정보를 찾을 수 없습니다.',
  server: '서버에 일시적인 문제가 있습니다. 잠시 후 다시 시도해 주세요.',
  unknown: '잠시 후 다시 시도해 주세요.',
};

/**
 * 실패 원인을 사용자 문구로 바꾼다.
 *
 * 서버가 내려준 문구를 그대로 쓰지 않는다 — 영문 스택이나 내부 메시지가
 * 그대로 노출된 적이 있어서다(utils/cachedResource.ts 의 같은 판단 참고).
 */
export function apiErrorMessage(error: any): string {
  return MESSAGE[classifyApiError(error)];
}

/**
 * 실패를 로그에 남기고 사용자에게 알린다.
 *
 * @param error    catch 로 받은 값
 * @param whatFailed 무엇이 안 됐는지 한 문장. 예: '내 정보를 저장하지 못했습니다.'
 *
 * 사용 예)
 *   api.post(...).then(...).catch((error) => {
 *     notifyApiError(error, '기록을 저장하지 못했습니다.');
 *   });
 */
export function notifyApiError(error: any, whatFailed: string): void {
  console.log(error);
  window.alert(`${whatFailed}\n${apiErrorMessage(error)}`);
}
